import React, {FC, ReactElement, useEffect, useRef, useCallback, useState, useMemo} from 'react';
import {
    DeviceConfig,
    RecognizableAndTranslatableLanguage,
    TranslatableAndSpeakableLanguage
} from '../contexts/Configuration';
import DeviceSelector from './DeviceSelector';
import {Slider, Switch} from '@mui/material';
import {updatePanelConfiguration} from '../reducers/sessionReducer';

import './Controller.scss';
import LanguageSelector from './LanguageSelector';
import VoiceSelector from './VoiceSelector';
import {useAppDispatch, useAppSelector} from '../hooks/storeHooks';
import SpeakerTest from "./audio/SpeakerTest";
import InputMonitorTest from "./audio/InputMonitorTest";
import {AudioLevel} from "./audio/AudioLevel";
import {AudioOutputLevel} from "./audio/AudioOutputLevel";
import {useEnersoundSessionContext} from "../contexts/SessionContextProvider";
import {getSessionContext} from "../contexts/SessionResolver";
import {applyLiveOutputVolume, isOutputDeviceActive} from "../utils/OutputVolume";

export enum DeviceType {IN = 'in', OUT = 'out'}

const Controller: FC<ControllerProps> = ({deviceType, id}): ReactElement => {

    const isInputController = deviceType === DeviceType.IN;

    // Single source of truth for gain: always read from Redux.
    // Range 0-200 (maps to Web Audio gain 0.0-2.0; 100 = 100% = gain 1.0).
    const gainLevel = useAppSelector((state) => isInputController
        ? state.session.inputGain
        : (state.session.outputGains[id] ?? 1)) * 100;

    // Local display state so the MUI Slider thumb moves immediately on drag.
    // Synced FROM gainLevel whenever Redux updates (e.g. popup slider changed it).
    const [displayGainLevel, setDisplayGainLevel] = useState<number>(gainLevel);
    useEffect(() => {
        setDisplayGainLevel(gainLevel);
    }, [gainLevel]);

    // Ref always holds the latest gainLevel so dispatchGain can read it
    // without being a dep of any useEffect.
    const gainLevelRef = useRef<number>(gainLevel);
    gainLevelRef.current = gainLevel;

    const [selectedLanguage, setSelectedLanguage] = useState<RecognizableAndTranslatableLanguage | TranslatableAndSpeakableLanguage | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo | null>();

    const sessionStarted = useAppSelector((state) => state.session.sessionStarted);
    const autoDetectLang = useAppSelector((state) => state.session.autoDetectLang);
    const availableOutputDevicesAmount = useAppSelector((state) => state.session.availableOutputDevicesAmount);
    const transcriptionPanels = useAppSelector((state) => state.session.transcriptionPanels);
    const outputs = useAppSelector((state) => state.session.outputs);
    const preSelectedOutputTextOnly = useAppSelector((state) => state.session.preSelectedOutputTextOnly);

    const [onlyText, setOnlyText] = useState(preSelectedOutputTextOnly[id] || false);
    const preSelectedOutputVoices = useAppSelector((state) => state.session.preSelectedOutputVoices);
    const [selectedVoice, setSelectedVoice] = useState<string | null>(preSelectedOutputVoices[id] || null);

    const isExtra = transcriptionPanels.find(p => id === p.id)?.isExtra || false;

    // const language = panel?.language ?? null;
    // const shortLanguageName = (language?.name ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim();
    const shortLanguageName = (selectedLanguage?.name ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim();

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Only apply default textOnly logic if there's NO stored value for this panel
        // This preserves user's explicit choice from previous sessions
        const hasStoredTextOnlyValue = preSelectedOutputTextOnly[id] !== undefined;
        if (hasStoredTextOnlyValue) {
            return; // Respect the stored value, don't override
        }

        const shouldCheckByDefaultTextOnly = isExtra && (outputs.filter(o => !o.textOnly).length > availableOutputDevicesAmount);
        if (!['panel_0', 'panel_1'].includes(id)) {
            setOnlyText(shouldCheckByDefaultTextOnly);
        }
    }, []);

    // Explicit dispatch function — called directly from event handlers, NOT from a useEffect.
    // Passing a value avoids any stale-closure issue with gainLevelRef.
    const dispatchGain = useCallback((value: number) => {
        const normalizedGain = Math.max(0, Math.min(2, value / 100));
        const deviceConfig: DeviceConfig = {
            type: deviceType,
            panelId: id,
            language: selectedLanguage,
            gain: normalizedGain,
            device: selectedDevice,
            ...(isInputController
                ? {autoDetectLang}
                : {textOnly: onlyText || false, voiceName: selectedVoice || undefined})
        };
        dispatch(updatePanelConfiguration(deviceConfig));
    }, [deviceType, id, selectedLanguage, selectedDevice, isInputController, autoDetectLang, onlyText, selectedVoice, dispatch]);

    // Dispatch whenever non-gain config fields change (language, device, textOnly, voice).
    // Gain changes are dispatched explicitly through dispatchGain — not here — to avoid
    // double-dispatch and the useEffect → Redux → re-render → useEffect loop.
    useEffect(() => {
        dispatchGain(gainLevelRef.current);
    }, [selectedLanguage, selectedDevice, onlyText, autoDetectLang, selectedVoice]); // eslint-disable-line react-hooks/exhaustive-deps

    const {session} = useEnersoundSessionContext()
    const lastOutputRebindRef = useRef<{ deviceId: string | null, isActive: boolean | null }>({
        deviceId: null,
        isActive: null
    });

    useEffect(() => {
        if (!sessionStarted || isInputController || !selectedVoice || !selectedLanguage) return;
        const translationKey = (selectedLanguage as TranslatableAndSpeakableLanguage).translationKey;
        const liveSession = getSessionContext();
        liveSession?.setOutputVoice(translationKey, selectedVoice);
    }, [selectedVoice]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!sessionStarted || isInputController || !selectedLanguage) {
            lastOutputRebindRef.current = {deviceId: null, isActive: null};
            return;
        }

        const translationKey = (selectedLanguage as TranslatableAndSpeakableLanguage).translationKey;
        const outputDevice = selectedDevice as MediaDeviceInfo | null | undefined;
        const currentDeviceId = outputDevice?.deviceId ?? null;
        const currentIsActive = isOutputDeviceActive(outputDevice);
        const prev = lastOutputRebindRef.current;

        // Apply this before priming the rebind baseline so a session that starts
        // with an already-disconnected remembered output is silent immediately.
        if (!currentIsActive && outputDevice) {
            applyLiveOutputVolume(translationKey, outputDevice, 0);
        }

        if (prev.deviceId === null && prev.isActive === null) {
            lastOutputRebindRef.current = {deviceId: currentDeviceId, isActive: currentIsActive};
            return;
        }

        const becameActive = prev.deviceId === currentDeviceId && prev.isActive === false && currentIsActive;
        const changedToActiveDevice = prev.deviceId !== currentDeviceId && !!currentDeviceId && currentIsActive;
        const outputGain = Math.max(0, Math.min(2, gainLevel / 100));

        if (process.env.NODE_ENV === 'development') {
            const stateChanged = prev.deviceId !== currentDeviceId || prev.isActive !== currentIsActive;
            if (stateChanged) {
                console.debug(`[Controller:${id}] output device state changed — becameActive=${becameActive} changedToActiveDevice=${changedToActiveDevice} prev=${JSON.stringify(prev)} current={deviceId:${currentDeviceId},isActive:${currentIsActive}}`);
            }
        }

        if ((becameActive || changedToActiveDevice) && outputDevice) {
            if (process.env.NODE_ENV === 'development') {
                console.debug(`[Controller:${id}] rebinding output device "${outputDevice.label}" (${currentDeviceId})`);
            }
            try {
                getSessionContext()?.setOutputDevice(translationKey, outputDevice, outputGain, becameActive);
            } catch {
            }
        }

        lastOutputRebindRef.current = {deviceId: currentDeviceId, isActive: currentIsActive};
    }, [sessionStarted, isInputController, selectedLanguage, selectedDevice, gainLevel]); // eslint-disable-line react-hooks/exhaustive-deps

    const outputAudioSource = useMemo<AudioNode | null>(() => {
        if (!session || !selectedLanguage || !sessionStarted) return null;
        return session.getText2Speech((selectedLanguage as TranslatableAndSpeakableLanguage).translationKey)
            ?.getAudioPlayer()?.getPostGainNode() ?? null;
    }, [session, selectedLanguage, sessionStarted, selectedDevice?.deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="controller-container">
            {/* Dropdown de idiomas */}
            <LanguageSelector
                id={id}
                isInputSelector={isInputController}
                onLanguageSelected={(selected) => {
                    setSelectedLanguage(selected);
                }}
                withSessionIcons={false}
            />
            {/* Dropdown de dispositivos */}
            <div className="level-control">
                <DeviceSelector
                    kind={isInputController ? 'audioinput' : 'audiooutput'}
                    labelPrefix={isInputController ? "In:" : "Out:"}
                    onDeviceSelected={(d) => setSelectedDevice(d)}
                    onlyTextEnabled={onlyText}
                    id={id}
                />

                {isInputController && selectedDevice?.deviceId && (
                    <InputMonitorTest
                        inputDeviceId={selectedDevice.deviceId}
                        volume={gainLevel}
                        // onVolumeChange={(v) => setInputGain(v)}
                        onVolumeChange={(v) => {
                            setDisplayGainLevel(v);
                            dispatchGain(v);
                        }}
                        languageName={shortLanguageName || undefined}
                        // iconOnly
                    />
                )}

                {!isInputController && selectedDevice?.deviceId && (
                    <SpeakerTest
                        speakerId={selectedDevice.deviceId}
                        volume={gainLevel}
                        onVolumeChange={(v) => {
                            setDisplayGainLevel(v);
                            dispatchGain(v);
                        }}
                    />
                )}
            </div>

            {/* Controles del dispositivo: volumen, output level, etc */}
            <div className="controller-settings">
                <div className="level-control">
                    <div className="level-control-label">
                        Volume
                    </div>
                    <Slider className="level-control-vol"
                            aria-label="Volume"
                            value={displayGainLevel}
                            step={10}
                            min={0}
                            max={200}            // allow up to 2.0 gain
                            onChange={(e, newValue) => {
                                const val = newValue as number;
                                setDisplayGainLevel(val);  // move thumb immediately
                                dispatchGain(val);         // persist to Redux
                            }}
                    />
                </div>

                {(isInputController && selectedDevice?.deviceId) && (
                    <div className="level-control">
                        <div className="level-control-label">Input level</div>
                        <AudioLevel deviceId={selectedDevice?.deviceId || null}
                                    gain={Math.max(0, Math.min(2, gainLevel / 100))} // pass normalized 0.0–2.0
                        />
                    </div>
                )}
                {(!isInputController && selectedDevice?.deviceId && selectedLanguage && session) && (
                    <div className="level-control">
                        <div className="level-control-label">Output level</div>
                        <AudioOutputLevel audioSource={outputAudioSource}/>
                    </div>
                )}

                {!isInputController && (
                    <div className="voice-textonly-row">
                        <VoiceSelector
                            id={id}
                            language={selectedLanguage as TranslatableAndSpeakableLanguage | null}
                            disabled={onlyText || !selectedLanguage}
                            onVoiceSelected={(voice) => setSelectedVoice(voice)}
                            selectedVoiceName={selectedVoice}
                        />
                        <div className="text-only-box">
                            <div className="text-only-label">Text only</div>
                            <Switch
                                checked={onlyText}
                                disabled={sessionStarted}
                                onChange={() => setOnlyText(!onlyText)}
                                size="small"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

type ControllerProps = {
    deviceType: DeviceType;
    id: string;
}

export default Controller
